import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useSearchParams, useLocation, useParams } from "react-router-dom";
import { Theme } from "@radix-ui/themes";
import type { Session } from "./lib/auth";

import { authClient } from "./lib/auth";
import { buildOAuthHandoffUrls } from "./lib/oauthHandoff";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import SignIn from "./pages/SignIn";
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import SlackLinkPage from "./pages/SlackLinkPage";
import Dashboard from "./pages/Dashboard";
import Flow360Browser from "./pages/Flow360Browser";
import FixSessionPage from "./pages/FixSessionPage";
import Settings from "./pages/Settings";
import ActivityDashboard from "./pages/ActivityDashboard";
import PrivacyNotice from "./pages/PrivacyNotice";
import ShareView from "./pages/ShareView";
import QRScan from "./pages/QRScan";
import CLIAuthPage from "./pages/CLIAuthPage";
import BobbinConnect from "./pages/BobbinConnect";
import Presentation from "./pages/Presentation";
import DocsPage from "./pages/DocsPage";
import MannequinPage from "./pages/Mannequin";
import Embed from "./pages/Embed";
import { CampaignConfigurator } from "./features/adaptive-sampling/configurator";
import { CampaignView } from "./features/adaptive-sampling/views";
import ThreadV3WorkbenchPage from "./features/thread-v3";
import ThreadV4WorkbenchPage from "./features/thread-v4";
import ThreadV5WorkbenchPage from "./features/thread-v5";
import WarpLineagePage from "./pages/WarpLineage";
import { ConversationViewer } from "./features/conversation";
import {
  DeckEditor,
  DeckList,
  FlexcomputeOnlyGuard,
  NewDeckPage,
} from "./features/decks";
import { recordPageView } from "./api/activity";
import { FeedbackReporter } from "./components/feedback/FeedbackReporter";

import "@radix-ui/themes/styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

function CLIPortRedirect() {
  const [params] = useSearchParams();
  const cliPort = params.get("cli_port");
  if (cliPort) {
    const next = new URLSearchParams(params);
    next.set("port", cliPort);
    next.delete("cli_port");
    return <Navigate to={`/auth/cli?${next.toString()}`} replace />;
  }
  return null;
}

function RedirectToDashboard() {
  return <Navigate to="/" replace />;
}

function RedirectProjectToV5() {
  const { projectId } = useParams();
  const location = useLocation();
  if (!projectId) return <Navigate to="/" replace />;
  return <Navigate to={`/v5/${encodeURIComponent(projectId)}${location.search}`} replace />;
}

function sanitizedPagePath(location: ReturnType<typeof useLocation>): string {
  const params = new URLSearchParams(location.search);
  for (const key of [
    "token",
    "_token",
    "invite",
    "code",
    "state",
    "session_state",
    "thread_auth_provider",
  ]) {
    params.delete(key);
  }
  const query = params.toString();
  return `${location.pathname}${query ? `?${query}` : ""}`;
}

function pageViewContext(location: ReturnType<typeof useLocation>): {
  project_id?: string;
  presentation_slug?: string;
} {
  const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const presentation = location.pathname.match(/^\/presentations\/([^/]+)/);
  if (presentation) {
    return { presentation_slug: decodeURIComponent(presentation[1]!) };
  }

  const project =
    location.pathname.match(/^\/project\/([^/]+)/) ??
    location.pathname.match(/^\/v2\/([^/]+)/) ??
    location.pathname.match(/^\/v3\/([^/]+)/) ??
    location.pathname.match(/^\/v4\/([^/]+)/) ??
    location.pathname.match(/^\/v5\/([^/]+)/) ??
    location.pathname.match(/^\/beta\/([^/]+)/) ??
    location.pathname.match(/^\/demo\/([^/]+)/);
  if (project) {
    const projectId = decodeURIComponent(project[1]!);
    return uuidLike.test(projectId) ? { project_id: projectId } : {};
  }

  const params = new URLSearchParams(location.search);
  const reviewProject = params.get("project");
  return reviewProject && uuidLike.test(reviewProject) ? { project_id: reviewProject } : {};
}

function AppRoutes() {
  const { theme } = useTheme();
  // useLocation() is required here: it causes AppRoutes to re-render on every
  // route change, which makes the inline flow360Auth computation stay fresh.
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState<Session | null>(null);
  const localDevBypass = import.meta.env.VITE_THREAD_ENV === "local";
  const hasE2EToken =
    localDevBypass ||
    (typeof window !== "undefined" && !!window.localStorage.getItem("thread_e2e_token"));
  const [loading, setLoading] = useState(true);
  const [flow360Auth, setFlow360Auth] = useState(false);
  const [authHandoffStarted, setAuthHandoffStarted] = useState(false);

  useEffect(() => {
    authClient.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setFlow360Auth(!s && !!sessionStorage.getItem("flow360_access_token"));
      setLoading(false);
    });

    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setFlow360Auth(!s && !!sessionStorage.getItem("flow360_access_token"));
    });

    const refreshFlow360Auth = () => {
      setFlow360Auth(!!sessionStorage.getItem("flow360_access_token"));
    };
    window.addEventListener("flow360-auth-ready", refreshFlow360Auth);
    window.addEventListener("storage", refreshFlow360Auth);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("flow360-auth-ready", refreshFlow360Auth);
      window.removeEventListener("storage", refreshFlow360Auth);
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (location.pathname.startsWith("/auth/")) return;
    if (location.pathname === "/privacy") return;
    if (location.pathname.startsWith("/presentations/")) return;

    const provider = searchParams.get("thread_auth_provider");
    if (
      !authHandoffStarted &&
      !session &&
      (provider === "google" || provider === "github")
    ) {
      setAuthHandoffStarted(true);
      const { redirectTo } = buildOAuthHandoffUrls({
        pathname: location.pathname,
        search: location.search,
        origin: window.location.origin,
        baseUrl: import.meta.env.BASE_URL,
      });

      authClient.auth.signInWithOAuth({
        provider,
        options: { redirectTo },
      }).catch((err) => {
        console.error("[auth] hosted auth handoff failed", err);
        setAuthHandoffStarted(false);
      });
      return;
    }

    const handle = window.setTimeout(() => {
      const context = pageViewContext(location);
      recordPageView({
        path: sanitizedPagePath(location),
        page_title: document.title,
        ...context,
        metadata: {
          flow360_only: flow360Auth && !session?.access_token,
        },
      }).catch((err) => {
        console.debug("[activity] page view register failed", err);
      });
    }, 0);

    return () => window.clearTimeout(handle);
  }, [
    authHandoffStarted,
    flow360Auth,
    loading,
    location.pathname,
    location.search,
    searchParams,
    session,
    session?.access_token,
  ]);

  if (loading) {
    return (
      <Theme appearance={theme} accentColor="blue" radius="medium">
        <FeedbackReporter />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <p style={{ color: "var(--gray-9)" }}>Loading...</p>
        </div>
      </Theme>
    );
  }

  const isAuthenticated = Boolean(session || flow360Auth || hasE2EToken);
  const requireThreadAuth = (element: ReactElement) => (
    isAuthenticated ? element : <SignIn />
  );

  return (
    <Theme appearance={theme} accentColor="blue" radius="medium">
      <FeedbackReporter />
      <CLIPortRedirect />
      <Routes>
        <Route path="/share/:shareId" element={<ShareView />} />
        <Route path="/embed/:projectId/:view" element={<Embed />} />
        <Route path="/qr" element={<QRScan />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/cli" element={<CLIAuthPage />} />
        <Route path="/auth/slack-link" element={<SlackLinkPage />} />
        <Route path="/connect/bobbin" element={<BobbinConnect />} />
        <Route path="/privacy" element={<PrivacyNotice />} />
        <Route path="/presentations/:slug" element={<Presentation />} />
        <Route path="/story/branching" element={<RedirectToDashboard />} />
        <Route path="/story/atomic-row" element={<RedirectToDashboard />} />
        <Route path="/review/atomic-unit" element={<RedirectToDashboard />} />
        <Route path="/review/v2-workbench" element={<RedirectToDashboard />} />
        <Route path="/project/:projectId/v2" element={<RedirectProjectToV5 />} />
        <Route path="/v2/:projectId" element={<RedirectProjectToV5 />} />
        <Route path="/v3/:projectId" element={requireThreadAuth(<ThreadV3WorkbenchPage />)} />
        <Route path="/v4/:projectId" element={requireThreadAuth(<ThreadV4WorkbenchPage />)} />
        <Route path="/v5/:projectId" element={requireThreadAuth(<ThreadV5WorkbenchPage />)} />
        {/* FXC-8538 Wave 3: Warp lineage explorer (shared @flexcompute/thread-graph). */}
        <Route path="/warp/:projectId" element={requireThreadAuth(<WarpLineagePage />)} />
        <Route path="/tsangpo" element={<Navigate to="/v3?fixture=tsangpo" replace />} />
        <Route path="/tsangpo-v4" element={<Navigate to="/v4?fixture=tsangpo" replace />} />
        <Route path="/tsangpo-v5" element={<Navigate to="/v5/fdaae53a-bf05-444b-bd5f-c13f1ed6ef64?source=run-table&graph=1" replace />} />
        <Route path="/beta/:projectId" element={<RedirectProjectToV5 />} />
        <Route path="/demo/:projectId" element={<RedirectProjectToV5 />} />

        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Routes>
                <Route path="/" element={<ThreadV5WorkbenchPage />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/projects" element={<ThreadV5WorkbenchPage />} />
                <Route path="/activity" element={<ActivityDashboard />} />
                <Route path="/conversations" element={<ConversationViewer />} />
                <Route path="/conversations/:id" element={<ConversationViewer />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/docs/*" element={<DocsPage />} />
                <Route path="/flow360" element={<Flow360Browser />} />
                <Route path="/mannequin/*" element={<MannequinPage session={session} />} />
                <Route path="/v3" element={<ThreadV3WorkbenchPage />} />
                <Route path="/v3/:projectId" element={<ThreadV3WorkbenchPage />} />
                <Route path="/v4" element={<ThreadV4WorkbenchPage />} />
                <Route path="/v4/:projectId" element={<ThreadV4WorkbenchPage />} />
                <Route path="/v5" element={<ThreadV5WorkbenchPage />} />
                <Route path="/v5/:projectId" element={<ThreadV5WorkbenchPage />} />
                <Route path="/tsangpo" element={<Navigate to="/v3?fixture=tsangpo" replace />} />
                <Route path="/tsangpo-v4" element={<Navigate to="/v4?fixture=tsangpo" replace />} />
                <Route path="/tsangpo-v5" element={<Navigate to="/v5/fdaae53a-bf05-444b-bd5f-c13f1ed6ef64?source=run-table&graph=1" replace />} />
                <Route
                  path="/project/:projectId/fix/:sessionId"
                  element={<FixSessionPage />}
                />
                <Route
                  path="/project/:projectId/v2"
                  element={<RedirectProjectToV5 />}
                />
                <Route
                  path="/project/:projectId/v3"
                  element={<ThreadV3WorkbenchPage />}
                />
                <Route
                  path="/project/:projectId/v4"
                  element={<ThreadV4WorkbenchPage />}
                />
                <Route
                  path="/project/:projectId/v5"
                  element={<ThreadV5WorkbenchPage />}
                />
                <Route
                  path="/project/:projectId"
                  element={<RedirectProjectToV5 />}
                />
                <Route path="/campaigns/new" element={<CampaignConfigurator />} />
                <Route path="/campaigns/:id" element={<CampaignView />} />
                <Route
                  path="/decks"
                  element={
                    <FlexcomputeOnlyGuard>
                      <DeckList />
                    </FlexcomputeOnlyGuard>
                  }
                />
                <Route
                  path="/decks/new"
                  element={
                    <FlexcomputeOnlyGuard>
                      <NewDeckPage />
                    </FlexcomputeOnlyGuard>
                  }
                />
                <Route
                  path="/decks/:deckId"
                  element={
                    <FlexcomputeOnlyGuard>
                      <DeckEditor />
                    </FlexcomputeOnlyGuard>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/sign-in" element={<SignIn />} />
                {/*
                  Render SignIn in place (don't <Navigate> to /sign-in) so the
                  deep-linked URL — e.g. /thread/mannequin — survives. SignIn's
                  rememberPostAuthDestination() reads window.location at mount;
                  redirecting first would overwrite it with /sign-in and the
                  user would land on home after login instead of where they
                  asked for. Matches the top-level requireThreadAuth() pattern.
                */}
                <Route path="*" element={<SignIn />} />
              </Routes>
            )
          }
        />
      </Routes>
    </Theme>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppRoutes />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
